import { useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ndd/ui";
import { useFeatureEnabled } from "@/lib/use-features";
import { FEATURE_FLAGS } from "../../../../shared/features";
import type {
  RdOption,
  WorkspaceOtherOwnerThreads,
  WorkspaceThreadSummary,
} from "../../../../shared/api-types";
import {
  ChatsSection,
  NavHeading,
  NavRow,
  OtherOwnersSection,
  type NavRowLink,
} from "./rd-chat-threads-nav";

export interface RdWorkspaceSidebarProps {
  rdId: string;
  rdSlug: string;
  rdName: string;
  /**
   * All RDs this user can access, loaded separately from the RD summary so
   * provider changes do not refetch the switcher list.
   */
  rdOptions: RdOption[];
  sessionCount: number;
  observationCount: number;
  meetingNoteCount: number;
  formSnapshotCount: number;
  threads: WorkspaceThreadSummary[];
  /** Admin-only: other supervisors' threads, grouped by owner. */
  otherOwners: WorkspaceOtherOwnerThreads[];
}

type RecordRow = {
  to: Exclude<NavRowLink, { to: "/rds/$rdId/chats/$chatId" }>["to"];
  /** Path segment under /rds/$rdId; empty for the RD landing itself. */
  segment: string;
  label: string;
  meta?: string;
};

function recordRows(
  counts: Pick<
    RdWorkspaceSidebarProps,
    "sessionCount" | "meetingNoteCount" | "observationCount" | "formSnapshotCount"
  >,
  metricsEnabled: boolean
): RecordRow[] {
  const rows: RecordRow[] = [
    {
      to: "/rds/$rdId/scorecards",
      segment: "scorecards",
      label: "Session Scorecards",
      meta: String(counts.sessionCount),
    },
    {
      to: "/rds/$rdId/meetings",
      segment: "meetings",
      label: "Meetings",
      meta: String(counts.meetingNoteCount),
    },
    {
      to: "/rds/$rdId/observations",
      segment: "observations",
      label: "Observations",
      meta: String(counts.observationCount),
    },
    {
      to: "/rds/$rdId/forms",
      segment: "forms",
      label: "Functional Forms",
      meta: String(counts.formSnapshotCount),
    },
  ];

  // The Snapshot and Metrics rows exist only while the supervisor-metrics
  // flag is confirmed on (NO-558, NO-560).
  if (metricsEnabled) {
    rows.unshift(
      { to: "/rds/$rdId", segment: "", label: "Snapshot" },
      { to: "/rds/$rdId/metrics", segment: "metrics", label: "Metrics" }
    );
  }

  return rows;
}

function RdSwitcherItem({
  rd,
  selected,
  onSelect,
}: {
  rd: RdOption;
  selected: boolean;
  onSelect: (rd: RdOption) => void;
}) {
  return (
    <CommandItem value={rd.name} onSelect={() => onSelect(rd)}>
      <Check className={cn("h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{rd.name}</span>
    </CommandItem>
  );
}

/**
 * The comp's white RD-switcher control, now a working picker: a popover
 * combobox over the cached RD list (in-memory filtering only).
 * Selecting routes to that RD's landing surface.
 */
function RdSwitcher({
  rdId,
  rdName,
  rdOptions,
}: {
  rdId: string;
  rdName: string;
  rdOptions: RdOption[];
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const selectRd = (rd: RdOption) => {
    setOpen(false);

    if (rd.id !== rdId) {
      void navigate({ to: "/rds/$rdId", params: { rdId: rd.slug } });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Radix's PopoverTrigger injects aria-expanded/aria-controls at
            runtime; declaring role="combobox" statically trips a11y lint
            without them. */}
        <button
          type="button"
          aria-label={`Switch RD — currently ${rdName}`}
          className="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-[10px] border border-border bg-card px-3 shadow-sm"
        >
          <strong className="truncate font-sans text-sm font-bold text-foreground">
            {rdName}
          </strong>
          <ChevronsUpDown className="h-[14px] w-[14px] shrink-0 text-subtle" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput placeholder="Find an RD…" />
          <CommandList>
            <CommandEmpty>No RD found.</CommandEmpty>
            <CommandGroup>
              {rdOptions.map((rd) => (
                <RdSwitcherItem
                  key={rd.id}
                  rd={rd}
                  selected={rd.id === rdId}
                  onSelect={selectRd}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** A segment row is active on its subtree; the landing row only on the exact path. */
function isRecordRowActive(row: RecordRow, base: string, pathname: string): boolean {
  return row.segment
    ? pathname.startsWith(`${base}/${row.segment}`)
    : pathname === base;
}

function RecordSection({
  rows,
  rdSlug,
  pathname,
}: {
  rows: RecordRow[];
  rdSlug: string;
  pathname: string;
}) {
  const base = `/rds/${rdSlug}`;

  return (
    <section>
      <NavHeading>RD record</NavHeading>
      <div className="flex flex-col gap-[1px]">
        {rows.map((row) => (
          <NavRow
            key={row.to}
            to={row.to}
            params={{ rdId: rdSlug }}
            active={isRecordRowActive(row, base, pathname)}
            meta={row.meta}
          >
            {row.label}
          </NavRow>
        ))}
      </div>
    </section>
  );
}

/**
 * The RD workspace sidebar per the comp contract (rd-workspace-comp.html):
 * RD switcher (name only, white control), "Back to all RDs", the RD record
 * group with right-aligned counts, and the Chats group ordered by recency
 * with Show more past five. Plain text rows, one active row across the
 * sidebar. Collapses behind an explicit control at narrow widths.
 */
export function RdWorkspaceSidebar({
  rdId,
  rdSlug,
  rdName,
  rdOptions,
  threads,
  otherOwners,
  ...counts
}: RdWorkspaceSidebarProps) {
  const pathname = useLocation().pathname;
  const [collapsed, setCollapsed] = useState(false);
  const metricsEnabled = useFeatureEnabled(FEATURE_FLAGS.supervisorMetrics);
  const rows = recordRows(counts, metricsEnabled);

  return (
    <div className="relative h-full shrink-0">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? "Show sidebar" : "Hide sidebar"}
        aria-expanded={!collapsed}
        className={cn(
          "absolute top-3 z-10 rounded-md p-[6px] text-subtle hover:bg-muted hover:text-foreground lg:hidden",
          collapsed ? "left-2" : "right-2"
        )}
      >
        <PanelLeft className="h-4 w-4" />
      </button>

      <aside
        aria-label="RD workspace navigation"
        className={cn(
          "flex h-full w-[268px] flex-col border-r border-border bg-muted",
          collapsed && "max-lg:hidden"
        )}
      >
        <div className="px-3 pb-1 pt-[14px]">
          <RdSwitcher rdId={rdId} rdName={rdName} rdOptions={rdOptions} />
          <Link
            to="/rds"
            className="mx-1 mt-2 inline-block rounded-md px-2 py-[3px] text-body-sm font-medium text-sand-500 hover:bg-bark/[0.05] hover:text-foreground"
          >
            Back to all RDs
          </Link>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-[18px] pt-2">
          <RecordSection rows={rows} rdSlug={rdSlug} pathname={pathname} />
          <ChatsSection threads={threads} rdSlug={rdSlug} pathname={pathname} />
          <OtherOwnersSection
            otherOwners={otherOwners}
            rdSlug={rdSlug}
            pathname={pathname}
          />
        </nav>
      </aside>
    </div>
  );
}
